/**
 * Shareable Code Modal Component
 *
 * @package
 */

import { __ } from '@wordpress/i18n';
import { useRef, useState, useEffect } from '@wordpress/element';
import { copyToClipboard } from '../helpers/copyToClipboard';
import Modal from './Modal';

const ShareableCodeModal = ( { onClose, shareableUrl, expirationDate, onCopyLink } ) => {
	const [ linkCopied, setLinkCopied ] = useState( false );
	const [ copyFailed, setCopyFailed ] = useState( false );
	const timeoutRefs = useRef( [] );

	useEffect( () => {
		return () => {
			timeoutRefs.current.forEach( id => clearTimeout( id ) );
			timeoutRefs.current = [];
		};
	}, [] );

	// Flash a state on for two seconds, tracking the timeout so unmount can clear it.
	const flash = setState => {
		setState( true );
		const timeoutId = setTimeout( () => {
			setState( false );
			timeoutRefs.current = timeoutRefs.current.filter( id => id !== timeoutId );
		}, 2000 );
		timeoutRefs.current.push( timeoutId );
	};

	const handleCopy = async ( text, setCopied, onCopied ) => {
		if ( ! await copyToClipboard( text ) ) {
			flash( setCopyFailed );
			return;
		}

		setCopyFailed( false );
		flash( setCopied );
		if ( onCopied ) {
			onCopied();
		}
	};

	const handleCopyLink = () => handleCopy( shareableUrl, setLinkCopied, onCopyLink );

	const modalTitleId = 'shareable-code-modal-title';
	const modalDescriptionId = 'shareable-code-modal-description';

	const formatExpirationDate = expiresAt => {
		if ( ! expiresAt ) {
			return null;
		}

		try {
			const expiryDate = new Date( expiresAt );
			const now = new Date();
			const daysUntilExpiry = Math.ceil( ( expiryDate - now ) / ( 1000 * 60 * 60 * 24 ) );

			if ( daysUntilExpiry < 0 ) {
				return __( 'Expired', 'at-vehicle-configurator' );
			}

			if ( daysUntilExpiry === 0 ) {
				return __( 'Expires today', 'at-vehicle-configurator' );
			}

			if ( daysUntilExpiry === 1 ) {
				return __( 'Expires tomorrow', 'at-vehicle-configurator' );
			}

			if ( daysUntilExpiry <= 7 ) {
				return __( 'Expires in {count} days', 'at-vehicle-configurator' ).replace( '{count}', daysUntilExpiry );
			}

			const formattedDate = expirationDate.toLocaleDateString( 'sv-SE', {
				year: 'numeric',
				month: 'long',
				day: 'numeric',
			} );

			return __( 'Expires on {date}', 'at-vehicle-configurator' ).replace( '{date}', formattedDate );
		} catch ( error ) {
			return null;
		}
	};

	const expirationText = formatExpirationDate( expirationDate );

	return (
		<Modal
			isOpen={ true }
			onClose={ onClose }
			title={ __( 'Share Configuration', 'at-vehicle-configurator' ) }
			titleId={ modalTitleId }
			descriptionId={ modalDescriptionId }
			className="configurator-shareable-code-modal"
			overlayClassName="configurator-shareable-code-modal-overlay"
		>
			<p id={ modalDescriptionId }>
				{ __( 'Anyone with this link can open your configuration.', 'at-vehicle-configurator' ) }
			</p>
			{ shareableUrl && (
				<div className="configurator-shareable-code-section">
					<div className="configurator-shareable-code-label">
						{ __( 'Sharing Link', 'at-vehicle-configurator' ) }
					</div>
					<div className="configurator-shareable-code-display">
						<code className="configurator-shareable-code-url" aria-label={ __( 'Sharing Link', 'at-vehicle-configurator' ) }>{ shareableUrl }</code>
						<button
							type="button"
							className="configurator-shareable-code-copy"
							onClick={ handleCopyLink }
							aria-label={ __( 'Copy link', 'at-vehicle-configurator' ) }
						>
							{ linkCopied ? __( 'Copied!', 'at-vehicle-configurator' ) : __( 'Copy', 'at-vehicle-configurator' ) }
						</button>
					</div>
				</div>
			) }
			<div role="status" aria-live="polite">
				{ copyFailed && (
					<span className="configurator-form-error">
						{ __( 'Could not copy to clipboard. Please copy it manually.', 'at-vehicle-configurator' ) }
					</span>
				) }
			</div>
			{ expirationText && (
				<div className="configurator-shareable-code-expiration">
					<span className="configurator-shareable-code-expiration-label">
						{ expirationText }
					</span>
				</div>
			) }
			<div className="configurator-shareable-code-actions">
				<button
					type="button"
					onClick={ onClose }
					className="configurator-button configurator-button-primary"
				>
					{ __( 'Close', 'at-vehicle-configurator' ) }
				</button>
			</div>
		</Modal>
	);
};

export default ShareableCodeModal;
